import React, { useRef, useState, useEffect } from 'react';
import { useFrame } from '@react-three/fiber';
import { useGameStore } from '../store/gameStore';
import { Vector3 } from 'three';
import { Text, Billboard } from '@react-three/drei';

interface JokeCharacterProps {
  jokes: string[];
}

const JokeCharacter: React.FC<JokeCharacterProps> = ({ jokes }) => {
  const { path, isPlaying, isPaused } = useGameStore();
  const [currentJoke, setCurrentJoke] = useState<string>('');
  const [showJoke, setShowJoke] = useState(false);
  const position = useRef(new Vector3());
  const pathIndex = useRef(0);
  const speed = 0.5; // Slower than enemies
  const jokeInterval = 5000; // Tell a joke every 5 seconds
  const lastJokeTime = useRef(0);

  // Initialize position at the start of the path
  useEffect(() => {
    if (path.length > 0) {
      position.current.copy(path[0]);
    }
  }, [path]);

  // Game loop for movement
  useFrame((_, delta) => {
    if (!isPlaying || isPaused) return;

    const currentTime = Date.now();
    
    // Tell a new joke every jokeInterval
    if (currentTime - lastJokeTime.current > jokeInterval) {
      const randomJoke = jokes[Math.floor(Math.random() * jokes.length)];
      setCurrentJoke(randomJoke);
      setShowJoke(true);
      lastJokeTime.current = currentTime;
    }

    // Move along the path
    const nextPoint = path[Math.min(pathIndex.current + 1, path.length - 1)];
    const direction = new Vector3().subVectors(nextPoint, position.current).normalize();
    const newPosition = position.current.clone().add(direction.multiplyScalar(speed * delta));

    // Check if we've reached the next point
    const distanceToNext = newPosition.distanceTo(nextPoint);
    if (distanceToNext < 0.2) {
      position.current.copy(nextPoint);
      pathIndex.current = (pathIndex.current + 1) % path.length;
    } else {
      position.current.copy(newPosition);
    }
  });

  return (
    <group position={position.current}>
      {/* Character model (simple sphere for now) */}
      <mesh>
        <sphereGeometry args={[0.5, 16, 16]} />
        <meshStandardMaterial color="#9932cc" />
      </mesh>

      {/* Joke text */}
      {showJoke && (
        <Billboard
          position={[0, 2, 0]}
          follow={true}
          lockX={false}
          lockY={false}
          lockZ={false}
        >
          <mesh position={[0, 0, -0.01]}>
            <planeGeometry args={[6, 2]} />
            <meshBasicMaterial color="black" transparent opacity={0.8} />
          </mesh>
          <Text
            position={[0, 0, 0]}
            fontSize={0.3}
            color="#ffffff"
            anchorX="center"
            anchorY="middle"
            maxWidth={5}
          >
            {currentJoke}
          </Text>
        </Billboard>
      )}
    </group>
  );
};

export default JokeCharacter; 